import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Clock, CalendarDays, ArrowRight, MessageCircle, Sparkles } from "lucide-react";
import { fileUrl, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LeadDialog } from "@/components/site/LeadDialog";
import { BookingDialog } from "@/components/site/BookingDialog";
import { RatingBadge } from "@/components/site/StarRating";
import { WishlistButton } from "@/components/site/WishlistButton";

export function TripCard({ trip, index = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay: (index % 3) * 0.08 }}
      className="group relative flex flex-col overflow-hidden rounded-2xl bg-card border border-border hover:shadow-2xl hover:shadow-black/10 md:hover:-translate-y-1 transition-[transform,box-shadow] duration-300"
      data-testid="trip-card"
    >
      <Link to={`/trips/${trip.slug}`} className="relative block h-56 overflow-hidden">
        <img src={fileUrl(trip.cover_image)} alt={trip.name} loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <Badge className="absolute top-3 left-3 bg-white/90 text-slate-900 hover:bg-white hover:text-slate-900 capitalize rounded-full">{trip.category?.replace("-", " ")}</Badge>
        {trip.special && <Badge className="absolute top-11 left-3 bg-gold text-black hover:bg-gold rounded-full" data-testid="card-special-tag">★ {trip.special_tag || "Safarshala Special"}</Badge>}
        {trip.creator && <Badge className={`absolute ${trip.special ? "top-[76px]" : "top-11"} left-3 bg-fuchsia-600 text-white hover:bg-fuchsia-600 rounded-full`} data-testid="card-creator-tag"><Sparkles className="h-3 w-3 mr-1" /> with {trip.creator_handle ? `@${String(trip.creator_handle).replace(/^@/, "")}` : trip.creator_name}</Badge>}
        {trip.departure_count > 0 && !trip.bookable && (
          <Badge className="absolute top-3 right-3 bg-destructive text-white rounded-full" data-testid="sold-out-badge">Sold Out</Badge>
        )}
        {trip.bookable && trip.min_seats_left != null && trip.min_seats_left <= 5 && (
          <Badge className="absolute top-3 right-3 bg-amber-500 text-white rounded-full animate-pulse" data-testid="seats-left-badge">Only {trip.min_seats_left} seat{trip.min_seats_left === 1 ? "" : "s"} left</Badge>
        )}
        {trip.departure_count > 0 && trip.bookable && !(trip.min_seats_left != null && trip.min_seats_left <= 5) && (
          <Badge className="absolute top-3 right-3 bg-secondary text-secondary-foreground rounded-full">{trip.departure_count} departures</Badge>
        )}
        <WishlistButton tripId={trip.id} className="absolute bottom-3 right-3 z-10" />
        <div className="absolute bottom-3 left-3 right-14 text-white">
          <div className="flex items-center justify-between gap-2">
            <p className="flex items-center gap-1 text-xs opacity-90"><MapPin className="h-3.5 w-3.5" /> {trip.destination}</p>
            <RatingBadge avg={trip.avg_rating} count={trip.review_count} />
          </div>
          <h3 className="font-heading text-2xl leading-tight">{trip.name}</h3>
        </div>
      </Link>

      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {trip.duration}</span>
          {trip.next_departure && <span className="flex items-center gap-1"><CalendarDays className="h-4 w-4" /> {trip.next_departure}</span>}
        </div>
        <div className="mt-3 flex items-end justify-between">
          <div>
            <p className="text-xs text-muted-foreground">Starting from</p>
            <p className="font-heading text-3xl text-primary">{inr(trip.starting_price)}</p>
            <p className="text-[11px] text-muted-foreground -mt-1" data-testid="card-sharing-note">per person · Quad sharing</p>
          </div>
          <Link to={`/trips/${trip.slug}`}>
            <Button variant="ghost" size="sm" className="rounded-full text-primary hover:text-primary hover:bg-primary/10" data-testid="view-details-btn">
              View Details <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <LeadDialog trip={trip}>
            <Button variant="outline" className="w-full rounded-full border-[#25D366] text-[#1da851] hover:bg-[#25D366]/10 hover:text-[#1da851]" data-testid="query-whatsapp-btn">
              <MessageCircle className="mr-1 h-4 w-4" /> Query
            </Button>
          </LeadDialog>
          <BookingDialog trip={trip}>
            <Button className="w-full rounded-full btn-shine" data-testid="book-now-btn" disabled={trip.departure_count > 0 && !trip.bookable}>{trip.departure_count > 0 && !trip.bookable ? "Sold Out" : "Book Now"}</Button>
          </BookingDialog>
        </div>
      </div>
    </motion.div>
  );
}
